import {useState, type ReactNode, type Ref} from "react";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {CheckIcon} from "@heroicons/react/24/outline";
import {ChevronDownIcon} from "@heroicons/react/24/solid";
import {getStoredOption, setStoredOption} from "./util/localStorageHelpers.ts";

export interface SplitButtonOption {
    id: string;
    label: string;
}

export interface SplitButtonProps {
    storageKey: string;
    options: SplitButtonOption[];
    defaultOptionId: string;
    disabled: boolean;
    pending?: boolean;
    onClick: (selectedOptionId: string) => void | Promise<void>;
    primaryLabel?: string;
    pendingLabel?: string;
    menuButtonAriaLabel?: string;
    labelForSelected?: (selected: SplitButtonOption | undefined) => string;
    menuAnchor?: "bottom start" | "bottom end" | "top start" | "top end";
    primaryRef?: Ref<HTMLButtonElement>;
    primaryContent?: ReactNode;
    selectedId?: string;
    onSelectedChange?: (selectedOptionId: string) => void;
    size?: "default" | "compact";
}

export function SplitButton({
    storageKey,
    options,
    defaultOptionId,
    disabled,
    pending = false,
    onClick,
    primaryLabel = "Continue",
    pendingLabel = "…",
    menuButtonAriaLabel = "More options",
    labelForSelected,
    menuAnchor = "bottom start",
    primaryRef,
    primaryContent,
    selectedId: controlledSelectedId,
    onSelectedChange,
    size = "default",
}: SplitButtonProps) {
    const compact = size === "compact";
    const validIds = options.map((o) => o.id);
    const fallbackId =
        options.find((o) => o.id === defaultOptionId)?.id ?? options[0]?.id ?? "";

    const [uncontrolledSelectedId, setSelectedId] = useState(() =>
        getStoredOption(storageKey, validIds, fallbackId),
    );
    const selectedId = controlledSelectedId ?? uncontrolledSelectedId;

    if (!options.length) {
        return null;
    }

    const resolvedSelected = validIds.includes(selectedId) ? selectedId : fallbackId;

    const buttonLabel = labelForSelected
        ? labelForSelected(options.find((o) => o.id === resolvedSelected))
        : primaryLabel;

    function selectOption(id: string) {
        if (!validIds.includes(id) || id === resolvedSelected) {
            return;
        }
        setSelectedId(id);
        setStoredOption(storageKey, id);
        onSelectedChange?.(id);
    }

    return (
        <div
            className={`inline-flex items-stretch overflow-hidden rounded-md shadow-sm ${disabled ? "bg-gray-300" : "bg-primary"}`}
        >
            <button
                ref={primaryRef}
                type="button"
                disabled={disabled}
                onClick={() => void onClick(resolvedSelected)}
                className={`inline-flex items-center justify-center bg-transparent font-semibold ${compact ? "h-9 px-4 text-[13px]" : "px-3.5 py-2 text-sm"} text-[#f4f9fc] hover:bg-darkprimary focus:outline-none disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent`}
            >
                {primaryContent ?? (pending ? pendingLabel : buttonLabel)}
            </button>
            <Menu as="div" className="relative flex min-w-0">
                <MenuButton
                    type="button"
                    disabled={disabled}
                    aria-label={menuButtonAriaLabel}
                    className={`inline-flex items-center justify-center border-l border-white/25 bg-transparent ${compact ? "px-2" : "px-2.5"} text-[#f4f9fc] hover:bg-darkprimary focus:outline-none disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent`}
                >
                    <ChevronDownIcon aria-hidden className={compact ? "h-4 w-4 shrink-0" : "h-5 w-5 shrink-0"} />
                </MenuButton>
                <MenuItems
                    transition
                    anchor={{ to: menuAnchor, gap: 4 }}
                    className="z-50 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
                >
                    {options.map((option) => {
                        const isSelected = option.id === resolvedSelected;
                        return (
                            <MenuItem key={option.id}>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100"
                                    onClick={() => selectOption(option.id)}
                                >
                                    <span className="min-w-0 flex-1">{option.label}</span>
                                    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                                        {isSelected ? (
                                            <CheckIcon className="h-4 w-4 text-primary" aria-hidden />
                                        ) : null}
                                    </span>
                                </button>
                            </MenuItem>
                        );
                    })}
                </MenuItems>
            </Menu>
        </div>
    );
}
